<template>
    <q-select filled v-model="route" use-input input-debounce="0" :label="label" :hint="hint" :options="routes"
        @filter="filterRoute" @update:modelValue="selectRoute" :dense="true" :clearable="true" :loading="loading"
        :readonly="readonly">
        <template v-slot:no-option>
            <q-item>
                <q-item-section class="text-grey">
                    Sin resultados
                </q-item-section>
            </q-item>
        </template>
    </q-select>
</template>

<script lang="ts">
import { defineComponent, onMounted, reactive, toRefs, watch } from "vue";
import { useQuasar } from "quasar";

import { OptionDto, FindAutocompleteDto } from "@/dtos";
import { errorNotify } from "@/helpers/utilities";
import routeService from "@/services/routes/route.service";

interface Props {
    clear?: boolean;
    label?: string;
    hint?: string;
    readonly?: boolean;
    company_id?: number;
    route_id?: number;
}

export default defineComponent({
    name: 'RouteAutocompleteComponent',
    props: {
        clear: { type: Boolean },
        label: { type: String },
        hint: { type: String },
        readonly: { type: Boolean, default: false },
        company_id: { type: Number },
        route_id: { type: Number }
    },
    emits: ["route_selected"],
    setup(props: Props, { emit }) {
        const $q = useQuasar();

        const config = reactive({
            loading: false,
            label: props.label ?? 'Ruta',
            hint: props.hint,
            company_id: props.company_id,
            searcher: ''
        });

        const data = reactive({
            route: '',
            routes: [] as OptionDto[],
            route_id: props.route_id
        });

        const fetchRoutes = async () => {
            try {
                config.loading = true;

                const params: FindAutocompleteDto = {
                    company_id: props.company_id,
                    searcher: config.searcher
                }

                const routes = await routeService.findAllAutocomplete(params);

                data.routes = [];
                for (const route of routes.data) {
                    const opt: OptionDto = {
                        label: route.long_name,
                        value: route.id
                    }

                    data.routes.push(opt);
                }

                if (data.route_id && data.route_id > 0) {
                    const route = data.routes.find(x => x.value == data.route_id);
                    if (route) {
                        data.route = route.label;
                        config.loading = false;
                        return;
                    }

                    await fetchRoute(data.route_id);
                }

                config.loading = false;
            } catch (error) {
                console.log(error);
                $q.notify(errorNotify('No se pudo obtener la información de las rutas'));
            }
        }

        const fetchRoute = async (route_id: number) => {
            try {
                const routeResult = await routeService.findOne(route_id);
                const opt: OptionDto = {
                    label: routeResult.data.long_name,
                    value: routeResult.data.id
                }

                data.routes.push(opt);
                data.route = opt.label;
            } catch (error) {
                console.log(error);
                $q.notify(errorNotify('No se pudo obtener la información de la ruta'));
            }
        }

        const selectRoute = (option: OptionDto) => {
            emit('route_selected', option);
        }

        const filterRoute = (val: string, update: (fn: () => void) => void) => {
            if (val === '' && data.routes.length == 0) {
                update(async () => {
                    config.searcher = '';
                    await fetchRoutes();
                })
                return;
            }

            update(async () => {
                const needle = val.toLowerCase();
                if (needle.length >= 2) {
                    config.searcher = needle;
                    data.route_id = undefined;
                    await fetchRoutes();
                }
            })
        }

        onMounted(async () => {
            await fetchRoutes();
        });

        watch([() => props.clear, () => props.route_id, () => props.company_id],
            async ([newVal, newValId, newCompanyId], [oldVal, oldRouteId, oldCompanyId]) => {
                if (newVal != oldVal) {
                    data.route = '';
                    data.route_id = undefined;

                    config.searcher = '';
                    fetchRoutes();
                }
                
                if (newCompanyId != oldCompanyId) {
                    data.route = '';
                    data.route_id = undefined;
                    config.company_id = undefined;

                    config.searcher = '';
                    fetchRoutes();
                    return;
                }

                if (newValId && newValId > 0 && (newCompanyId == oldCompanyId || oldCompanyId == 0)) {
                    const route = data.routes.find(x => x.value == newValId);
                    if (route) {
                        data.route = route.label;
                        return;
                    }

                    await fetchRoute(newValId);
                    return;
                }
                
                await fetchRoutes();
            });

        return {
            ...toRefs(config),
            ...toRefs(data),
            filterRoute,
            selectRoute
        }
    },
})
</script>
